import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/LoginPage.css";
import loginImage from "../assets/login.jpg";
import { API_BASE_URL } from "../config/api";

export default function VerificationCode() {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer l'email/téléphone depuis la navigation
  const contact = location.state?.contact || '';
  const isEmail = contact.includes('@');
  const maskedContact = isEmail 
    ? contact.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : contact.replace(/(\d{2})(\d*)(\d{2})/, '$1******$3');

  useEffect(() => {
    // Focus sur le premier input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    // Countdown pour le renvoi du code
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleChange = (index, value) => {
    // Accepter uniquement les chiffres
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Passer au champ suivant automatiquement
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Retourner au champ précédent avec Backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    pastedData.split('').forEach((char, i) => {
      if (i < 6) newCode[i] = char;
    });
    setCode(newCode);

    // Focus sur le dernier champ rempli ou le suivant
    const lastFilledIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      setError('Veuillez entrer le code complet à 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          contact,
          code: fullCode 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Si c'est pour la réinitialisation du mot de passe
        if (location.state?.resetPassword) {
          navigate('/reinitialiser-mot-de-passe', { 
            state: { 
              contact,
              token: data.resetToken 
            } 
          });
        } else {
          // Connexion normale avec 2FA
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          navigate('/dashboard');
        }
      } else {
        setError(data.message || 'Code invalide');
      }
    } catch (error) {
      console.error('Erreur de vérification:', error);
      setError('Erreur serveur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contact }),
      });

      if (response.ok) {
        setCountdown(60);
        setCanResend(false);
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error('Erreur lors du renvoi:', error);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="image-wrapper">
          <img src={loginImage} alt="Verification" className="login-image" />
        </div>
        <div className="overlay-text">
          <h2>Espace Administrateur</h2>
          <p>
            Gérez vos prestations, vos locations et vos utilisateurs depuis
            votre espace administrateur.
          </p>
        </div>
      </div>

      <div className="login-right">
        <div className="login-box">
          <h1 className="logo">WaaGo</h1>
          <h2>Code de vérification</h2>
          <p className="subtitle">
            Veuillez entrer le code de confirmation de votre compte envoyé
            {isEmail ? ' au ' : ' au '}
            <strong>{maskedContact}</strong>
          </p>

          <form className="login-form" onSubmit={handleVerify}>
            <label>Code</label>
            <div className="verification-code-inputs" style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  style={{
                    width: '50px',
                    height: '50px',
                    textAlign: 'center',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                />
              ))}
            </div>

            {error && (
              <div className="error-message" style={{
                color: '#e53935',
                marginBottom: '15px',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Vérification...' : 'Vérifier'}
            </button>

            <div className="resend-section" style={{
              textAlign: 'center',
              marginTop: '20px'
            }}>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Aucun code reçu ?{' '}
                {canResend ? (
                  <span 
                    onClick={handleResendCode}
                    style={{ 
                      color: '#1a56db', 
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Renvoyer
                  </span>
                ) : (
                  <span style={{ color: '#999' }}>
                    Renvoyer dans {countdown}s
                  </span>
                )}
              </p>
            </div>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <span 
                onClick={() => navigate(-1)}
                style={{ 
                  color: '#e53935', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Se connecter
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
